import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

export type ToastState = {
  title: string;
  body: string;
  followups?: string[];
} | null;

export function Toast({
  toast,
  theme = "dark",
  onClose,
  onPickFollowup,
}: {
  toast: ToastState;
  theme?: "light" | "dark";
  onClose: () => void;
  onPickFollowup: (text: string) => void;
}) {
  const isDark = theme === "dark";
  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.18 }}
          className={
            "fixed bottom-6 right-6 z-50 w-[420px] rounded-2xl border p-4 backdrop-blur-xl " +
            (isDark
              ? "border-white/15 bg-[#0a0c24]/90 shadow-[0_30px_90px_-50px_rgba(0,0,0,0.95)]"
              : "border-slate-200 bg-white shadow-xl")
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={"text-sm font-semibold " + (isDark ? "text-white" : "text-slate-900")}>
                {toast.title}
              </div>
              <div
                className={
                  "mt-2 whitespace-pre-wrap text-xs leading-relaxed " +
                  (isDark ? "text-white/70" : "text-slate-600")
                }
              >
                {toast.body}
              </div>
            </div>
            <button
              onClick={onClose}
              className={
                "grid h-8 w-8 place-items-center rounded-full border transition-colors " +
                (isDark
                  ? "border-white/10 bg-white/5 hover:bg-white/10"
                  : "border-slate-200 bg-white hover:bg-slate-100")
              }
              aria-label="close"
            >
              <X className={"h-4 w-4 " + (isDark ? "text-white/70" : "text-slate-600")} />
            </button>
          </div>
          {toast.followups?.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {toast.followups.map((f) => (
                <button
                  key={f}
                  onClick={() => onPickFollowup(f)}
                  className={
                    "rounded-full border px-3 py-1 text-[11px] transition-colors " +
                    (isDark
                      ? "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
                      : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100")
                  }
                >
                  {f}
                </button>
              ))}
            </div>
          ) : null}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
